import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Admin.css';
import API_BASE from '../config';

function RoleManagement() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [hierarchy, setHierarchy] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [selectedRoleToAssign, setSelectedRoleToAssign] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch available roles
      const rolesRes = await fetch(`${API_BASE}/admin/available-roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch role hierarchy
      const hierarchyRes = await fetch(`${API_BASE}/admin/role-hierarchy`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch users
      const usersRes = await fetch(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (rolesRes.ok && hierarchyRes.ok && usersRes.ok) {
        const rolesData = await rolesRes.json();
        const hierarchyData = await hierarchyRes.json();
        const usersData = await usersRes.json();

        setRoles(rolesData.roles || []);
        setHierarchy(hierarchyData.hierarchy || {});
        setUsers(usersData.users || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/admin/user-roles/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedUser(data.user);
        setUserRoles(data.user.roles || []);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
    }
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();

    if (!selectedRoleToAssign) {
      alert('Please select a role');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/admin/assign-role`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          roleName: selectedRoleToAssign
        })
      });

      if (response.ok) {
        alert('Role assigned successfully');
        setSelectedRoleToAssign('');
        handleSelectUser(selectedUser); // Refresh
        fetchData(); // Refresh users list
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to assign role');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleRemoveRole = async (roleId) => {
    if (!window.confirm('Remove this role from user?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE}/admin/remove-role/${selectedUser.id}/${roleId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        alert('Role removed successfully');
        handleSelectUser(selectedUser); // Refresh
        fetchData(); // Refresh users list
      } else {
        alert('Failed to remove role');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getAccessLevelColor = (level) => {
    switch(level) {
      case 'SUPER_ADMIN': return '#d32f2f';
      case 'DATA_ADMIN': return '#ff9800';
      case 'DATA_PROCESSOR': return '#2196f3';
      default: return '#4caf50';
    }
  };

  if (loading) return <div className="admin-container"><p>Loading...</p></div>;

  return (
    <div className="admin-container">
      <div className="page-header">
        <button onClick={() => navigate('/admin')} className="btn-back-nav" style={{ marginBottom: 12 }}>← Back</button>
        <h1>Role Management & Hierarchy</h1>
        <p>Manage user roles and permissions across the organization</p>
      </div>

      {/* Role Hierarchy Info */}
      {hierarchy && (
        <div className="section">
          <h2>Role Hierarchy Overview</h2>
          <div className="hierarchy-grid">
            {Object.values(hierarchy).sort((a, b) => a.level - b.level).map((role, idx) => (
              <div key={idx} className="hierarchy-card" style={{ borderTopColor: getAccessLevelColor(role.name) }}>
                <div className="hierarchy-level">Level {role.level}</div>
                <h3>{role.title}</h3>
                <p className="hierarchy-description">{role.description}</p>
                <div className="hierarchy-access">
                  <strong>Access Scope:</strong>
                  <p>{role.access}</p>
                </div>
                <div className="hierarchy-use-cases">
                  <strong>Use Cases:</strong>
                  <ul>
                    {role.useCases.map((useCase, i) => (
                      <li key={i}>{useCase}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Roles Table */}
      <div className="section">
        <h2>Available Roles & Permissions</h2>
        <p>System roles with their assigned permissions</p>
        
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Role Name</th>
                <th>Description</th>
                <th>Total Permissions</th>
                <th>Global</th>
                <th>Department</th>
                <th>User</th>
              </tr>
            </thead>
            <tbody>
              {roles.map(role => (
                <tr key={role.id}>
                  <td><strong>{role.name}</strong></td>
                  <td>{role.description}</td>
                  <td>{role.permissionSummary.total}</td>
                  <td>{role.permissionSummary.byScope.GLOBAL}</td>
                  <td>{role.permissionSummary.byScope.DEPARTMENT}</td>
                  <td>{role.permissionSummary.byScope.USER}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Role Assignment */}
      <div className="section">
        <h2>Assign Roles to Users</h2>
        
        <div className="role-assignment-container">
          {/* User List */}
          <div className="user-list-section">
            <h3>Select User</h3>
            <div className="user-list">
              {users.map(user => (
                <div
                  key={user.id}
                  className={`user-item ${selectedUser?.id === user.id ? 'active' : ''}`}
                  onClick={() => handleSelectUser(user)}
                >
                  <div className="user-info">
                    <div className="user-name">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="user-email">{user.email}</div>
                  </div>
                  <div className="user-access-level" style={{ backgroundColor: getAccessLevelColor(user.iso27001UserType) }}>
                    {user.iso27001UserType}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* User Details & Role Assignment */}
          {selectedUser && (
            <div className="user-details-section">
              <h3>User Details & Roles</h3>
              
              <div className="user-detail-card">
                <div className="detail-row">
                  <strong>Name:</strong> {selectedUser.firstName} {selectedUser.lastName}
                </div>
                <div className="detail-row">
                  <strong>Email:</strong> {selectedUser.email}
                </div>
                <div className="detail-row">
                  <strong>Department:</strong> {selectedUser.department?.name || 'N/A'}
                </div>
                <div className="detail-row">
                  <strong>Access Level:</strong>
                  <span style={{ 
                    backgroundColor: getAccessLevelColor(selectedUser.accessLevel),
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    marginLeft: '10px'
                  }}>
                    {selectedUser.accessLevel}
                  </span>
                </div>
              </div>

              {/* Current Roles */}
              <div className="roles-section">
                <h4>Current Roles ({userRoles.length})</h4>
                {userRoles.length > 0 ? (
                  <div className="roles-list">
                    {userRoles.map(role => (
                      <div key={role.id} className="role-item">
                        <div>
                          <strong>{role.name}</strong>
                          <p>{role.description}</p>
                          <small>{role.permissionsCount} permissions</small>
                        </div>
                        <button
                          onClick={() => handleRemoveRole(role.id)}
                          className="btn-small btn-danger"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">No roles assigned</p>
                )}
              </div>

              {/* Assign New Role */}
              <div className="assign-role-section">
                <h4>Assign New Role</h4>
                <form onSubmit={handleAssignRole} className="assign-form">
                  <select
                    value={selectedRoleToAssign}
                    onChange={(e) => setSelectedRoleToAssign(e.target.value)}
                    required
                  >
                    <option value="">-- Select Role --</option>
                    {roles.map(role => (
                      <option key={role.id} value={role.name}>
                        {role.name} - {role.description}
                      </option>
                    ))}
                  </select>
                  <button type="submit" className="btn-primary">
                    Assign Role
                  </button>
                </form>
              </div>

              {/* User Permissions */}
              <div className="permissions-section">
                <h4>User Permissions ({selectedUser.permissions.length})</h4>
                <div className="permissions-list">
                  {selectedUser.permissions.map((p, idx) => (
                    <div key={idx} className="permission-tag">
                      <strong>{p.resource}:{p.action}</strong>
                      <small>{p.scope}</small>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Role Assignment Guide */}
      <div className="section info-section">
        <h2>Role Assignment Guidelines</h2>
        <div className="guidelines-grid">
          <div className="guideline-card">
            <h4>🔴 SUPER_ADMIN</h4>
            <p>Only for tool developers and platform support staff who need to maintain the system.</p>
            <ul>
              <li>Access to all features</li>
              <li>Can manage all departments</li>
              <li>Can create/delete roles</li>
            </ul>
          </div>

          <div className="guideline-card">
            <h4>🟠 DATA_ADMIN (Department-Specific)</h4>
            <p>Department heads or managers who oversee their department's data and access.</p>
            <ul>
              <li>HR Admin - Manage employees, onboarding/offboarding</li>
              <li>IT Admin - Manage IT systems and access</li>
              <li>Recruitment Admin - Manage recruitment process</li>
            </ul>
          </div>

          <div className="guideline-card">
            <h4>🔵 DATA_PROCESSOR</h4>
            <p>Staff who process data as part of their job but don't manage other users.</p>
            <ul>
              <li>Customer support staff</li>
              <li>Data entry personnel</li>
              <li>Document handlers</li>
            </ul>
          </div>

          <div className="guideline-card">
            <h4>🟢 NORMAL_USER</h4>
            <p>Standard employees with minimal access to their own information.</p>
            <ul>
              <li>Can view own profile</li>
              <li>Can view employee directory</li>
              <li>Limited to personal data</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleManagement;
